import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputAdornment,
  Stack,
  TextField,
  Button,
} from "@mui/material";
import { ReactNode } from "react";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { MeetingTypeInfoSelect } from "./MeetingTypeInfoSelect";

export interface FilterTypes {
  meetingType?: string;
  search?: string;
  status?: string;
  hostId?: string;
  roomKey?: string;
}

export const defaultFilters: FilterTypes = {
  meetingType: "All",
  search: "",
  status: "All",
  hostId: "All",
  roomKey: "All",
};

export interface HostWithId {
  id: string;
  name: string;
}

interface FilterProps {
  filters: FilterTypes;
  hosts: HostWithId[];
  /** Distinct continuous room keys present in the loaded meetings. */
  roomKeys: string[];
  onMeetingTypeChange: (event: string) => void;
  onStatusChange: (event: SelectChangeEvent<string>, child: ReactNode) => void;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHostChange: (event: SelectChangeEvent<string>, child: ReactNode) => void;
  onRoomKeyChange: (
    event: SelectChangeEvent<string>,
    child: ReactNode
  ) => void;
  hasFilters: boolean;
  onClearFilters: () => void;
}

export const Filters: React.FC<FilterProps> = ({
  filters,
  hosts,
  roomKeys,
  onMeetingTypeChange,
  onStatusChange,
  onSearchChange,
  onHostChange,
  onRoomKeyChange,
  hasFilters,
  onClearFilters,
}) => {
  const { meetingType, search, status, hostId, roomKey } = filters || {};
  return (
    <Stack gap={1.5} margin="0 10px">
      <TextField
        key="search"
        value={search}
        onChange={onSearchChange}
        placeholder="Search meetings"
        size="small"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchOutlined />
            </InputAdornment>
          ),
        }}
      />
      <FormControl size="small" fullWidth key="status">
        <InputLabel>Status</InputLabel>
        <Select value={status} onChange={onStatusChange} label="Status">
          <MenuItem value="All" key="all">
            All
          </MenuItem>
          <MenuItem value="Missing data" key="missingData">
            Missing data
          </MenuItem>
          <MenuItem value="NotVisible" key="notVisible">
            Not visible
          </MenuItem>
        </Select>
      </FormControl>
      <MeetingTypeInfoSelect
        currentMeetingTypeId={meetingType ?? "All"}
        onMeetingTypeChange={onMeetingTypeChange}
        includeAllOption={true}
        formControlProps={{
          size: "small",
          fullWidth: true,
        }}
        selectProps={{
          label: "Meeting Type",
        }}
      />
      <FormControl size="small" fullWidth key="host">
        <InputLabel>Host</InputLabel>
        <Select value={hostId ?? "All"} onChange={onHostChange} label="Host">
          <MenuItem value="All" key="all">
            All
          </MenuItem>
          {hosts.map((h) => (
            <MenuItem value={h.id} key={h.id}>
              {h.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {roomKeys.length > 0 && (
        <FormControl size="small" fullWidth key="roomKey">
          <InputLabel>Room</InputLabel>
          <Select
            value={roomKey ?? "All"}
            onChange={onRoomKeyChange}
            label="Room"
          >
            <MenuItem value="All" key="all">
              All
            </MenuItem>
            {roomKeys.map((key) => (
              <MenuItem value={key} key={key}>
                {key}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {hasFilters && (
        <Button
          onClick={onClearFilters}
          startIcon={<CloseOutlined />}
          sx={{ alignSelf: "flex-start" }}
        >
          Clear all filters
        </Button>
      )}
    </Stack>
  );
};
